import { SEOHead } from '@/components/seo/SEOHead';

const LAST_UPDATED = 'February 20, 2026';
const COMPANY_NAME = 'Rogue Move Media LLC';
const COMPANY_ADDRESS = '30 N Gould St #55327, Sheridan, WY 82801';
const SUPPORT_EMAIL = 'support@rooflyzer.pro';

export const EulaPage = () => {
  return (
    <>
      <SEOHead
        title="End User License Agreement (EULA)"
        description="End User License Agreement for the Rooflyzer OS platform. Read the terms governing your license to use Rooflyzer software and services."
        canonicalUrl="/eula"
        robots="index, follow"
      />

      <section className="pt-24 pb-24 px-4">
        <div className="max-w-3xl mx-auto prose prose-invert prose-sm">
          <h1 className="text-3xl font-bold text-white mb-2">End User License Agreement</h1>
          <p className="text-white/50 text-sm mb-8">Last updated: {LAST_UPDATED}</p>

          <p className="text-white/80">
            This End User License Agreement (&quot;EULA&quot; or &quot;Agreement&quot;) is a legal
            agreement between you (&quot;User,&quot; &quot;you,&quot; or &quot;your&quot;) and{' '}
            {COMPANY_NAME} (&quot;Company,&quot; &quot;we,&quot; &quot;us,&quot; or
            &quot;our&quot;), governing your use of the Rooflyzer OS platform, including all related
            software, applications, updates, and services (collectively, the &quot;Software&quot;).
          </p>
          <p className="text-white/80 mt-4">
            BY ACCESSING OR USING THE SOFTWARE, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND
            AGREE TO BE BOUND BY THIS AGREEMENT. IF YOU DO NOT AGREE, DO NOT USE THE SOFTWARE.
          </p>

          {/* Section 1 */}
          <h2 className="text-xl font-semibold text-white mt-8 mb-3">1. License Grant</h2>
          <p className="text-white/80">
            Subject to the terms of this Agreement, {COMPANY_NAME} grants you a limited,
            non-exclusive, non-transferable, revocable license to access and use the Rooflyzer OS
            platform solely for your internal business operations related to roofing contracting,
            sales, project management, and related activities.
          </p>
          <p className="text-white/80 mt-2">This license does not include the right to:</p>
          <ul className="text-white/70 space-y-1">
            <li>Sublicense, sell, resell, or redistribute the Software</li>
            <li>Modify, adapt, translate, or create derivative works of the Software</li>
            <li>Reverse-engineer, decompile, disassemble, or attempt to derive the source code</li>
            <li>Remove or alter any proprietary notices, labels, or marks</li>
            <li>Use the Software to build a competitive product or service</li>
          </ul>

          {/* Section 2 */}
          <h2 className="text-xl font-semibold text-white mt-8 mb-3">
            2. Account Registration &amp; Security
          </h2>
          <p className="text-white/80">
            To use the Software, you must create an account and provide accurate, current
            information. You are responsible for:
          </p>
          <ul className="text-white/70 space-y-1">
            <li>Maintaining the confidentiality of your account credentials</li>
            <li>All activities that occur under your account</li>
            <li>Notifying us immediately of any unauthorized access or use</li>
            <li>Ensuring that all users within your organization comply with this Agreement</li>
          </ul>
          <p className="text-white/80 mt-2">
            Rooflyzer OS operates as a multi-tenant platform. Each organization (&quot;tenant&quot;)
            has its own isolated data environment. You may not attempt to access data belonging to
            other tenants.
          </p>

          {/* Section 3 */}
          <h2 className="text-xl font-semibold text-white mt-8 mb-3">3. Permitted Use</h2>
          <p className="text-white/80">
            The Software is designed for roofing contractors and related businesses. Permitted uses
            include:
          </p>
          <ul className="text-white/70 space-y-1">
            <li>Customer relationship management (CRM) and lead tracking</li>
            <li>Generating quotes, estimates, proposals, and contracts</li>
            <li>Scheduling appointments and managing production workflows</li>
            <li>Processing payments and managing financial records</li>
            <li>Marketing campaigns (email, SMS) to your customers</li>
            <li>Property data analysis and roof measurements</li>
            <li>Connecting third-party integrations (QuickBooks, payment processors, etc.)</li>
          </ul>

          {/* Section 4 */}
          <h2 className="text-xl font-semibold text-white mt-8 mb-3">
            4. Third-Party Integrations
          </h2>
          <p className="text-white/80">
            The Software may connect to third-party services, including but not limited to:
          </p>
          <ul className="text-white/70 space-y-1">
            <li>
              <strong className="text-white/90">QuickBooks Online</strong> — Accounting, invoicing,
              and financial data synchronization
            </li>
            <li>
              <strong className="text-white/90">Payment Processors</strong> — Stripe and other
              payment gateways for transaction processing
            </li>
            <li>
              <strong className="text-white/90">Mapping Services</strong> — Mapbox, Google Maps for
              property visualization and territory management
            </li>
            <li>
              <strong className="text-white/90">Communication Services</strong> — Email and SMS
              delivery for customer communications
            </li>
            <li>
              <strong className="text-white/90">Property Data Providers</strong> — ATTOM and similar
              services for property and roof data
            </li>
          </ul>
          <p className="text-white/80 mt-4">By connecting a third-party integration, you:</p>
          <ul className="text-white/70 space-y-1">
            <li>
              Authorize Rooflyzer to access, retrieve, and synchronize data between your accounts
            </li>
            <li>
              Acknowledge that the third-party service is governed by its own terms of service and
              privacy policy
            </li>
            <li>
              Understand that Rooflyzer is not responsible for the availability, accuracy, or
              security practices of third-party services
            </li>
            <li>
              May disconnect any integration at any time, which will revoke Rooflyzer&apos;s access
              to that service
            </li>
          </ul>
          <p className="text-white/80 mt-4">
            <strong className="text-white/90">QuickBooks-Specific Terms:</strong> When you connect
            QuickBooks Online, Rooflyzer accesses your company information, customer records,
            invoices, and payment data solely for the purpose of synchronizing financial data within
            the platform. OAuth tokens are stored encrypted at rest and are automatically revoked
            when you disconnect. Rooflyzer does not sell, share, or use your QuickBooks data for any
            purpose other than providing the Service.
          </p>

          {/* Section 5 */}
          <h2 className="text-xl font-semibold text-white mt-8 mb-3">
            5. Data Ownership &amp; Processing
          </h2>
          <p className="text-white/80">
            <strong className="text-white/90">Your Data:</strong> You retain all ownership rights to
            the data you input, upload, or generate through the Software (&quot;Your Data&quot;).
            This includes customer records, quotes, contracts, invoices, and all business data.
          </p>
          <p className="text-white/80 mt-2">
            <strong className="text-white/90">Our License:</strong> You grant {COMPANY_NAME} a
            limited, non-exclusive license to store, process, transmit, and display Your Data solely
            for the purpose of providing, maintaining, and improving the Software.
          </p>
          <p className="text-white/80 mt-2">
            <strong className="text-white/90">Data Processing:</strong> We process Your Data in
            accordance with our{' '}
            <a href="/privacy" className="text-emerald-400 hover:underline">
              Privacy Policy
            </a>
            . We implement industry-standard security measures including encryption at rest
            (AES-256), encryption in transit (TLS 1.2+), and multi-tenant data isolation through
            Row-Level Security policies.
          </p>
          <p className="text-white/80 mt-2">
            <strong className="text-white/90">Data Portability:</strong> You may export Your Data at
            any time through the platform&apos;s built-in export features.
          </p>

          {/* Section 6 */}
          <h2 className="text-xl font-semibold text-white mt-8 mb-3">6. Restrictions</h2>
          <p className="text-white/80">You shall not:</p>
          <ul className="text-white/70 space-y-1">
            <li>
              Use the Software for any unlawful purpose or in violation of any applicable laws
            </li>
            <li>Attempt to gain unauthorized access to other users&apos; data or accounts</li>
            <li>Upload malicious code, viruses, or harmful content</li>
            <li>Interfere with or disrupt the integrity or performance of the Software</li>
            <li>Use automated tools to scrape, crawl, or extract data from the Software</li>
            <li>Circumvent, disable, or interfere with security features of the Software</li>
            <li>Use the Software for spamming or unsolicited communications</li>
            <li>Share your account credentials with unauthorized individuals</li>
          </ul>

          {/* Section 7 */}
          <h2 className="text-xl font-semibold text-white mt-8 mb-3">
            7. Payment Terms &amp; Billing
          </h2>
          <p className="text-white/80">
            Paid plans are billed monthly or annually in advance. All fees are non-refundable except
            as required by law. We may change pricing with 30 days&apos; written notice; changes
            take effect at your next billing cycle. If payment fails, we may suspend access after a
            7-day grace period. Free plans may have usage limitations.
          </p>

          {/* Section 8 */}
          <h2 className="text-xl font-semibold text-white mt-8 mb-3">8. Intellectual Property</h2>
          <p className="text-white/80">
            The Software, including all code, design, text, graphics, logos, trademarks, and
            documentation, is the exclusive property of {COMPANY_NAME} and is protected by
            copyright, trademark, and other intellectual property laws. This Agreement does not
            transfer any ownership rights to you.
          </p>

          {/* Section 9 */}
          <h2 className="text-xl font-semibold text-white mt-8 mb-3">
            9. Service Availability &amp; Support
          </h2>
          <p className="text-white/80">
            We strive for 99.9% uptime but do not guarantee uninterrupted access. Scheduled
            maintenance will be communicated with reasonable advance notice. We are not liable for
            downtime caused by factors outside our control (force majeure, third-party outages,
            internet disruptions, etc.).
          </p>

          {/* Section 10 */}
          <h2 className="text-xl font-semibold text-white mt-8 mb-3">
            10. Termination &amp; Data Export
          </h2>
          <p className="text-white/80">
            <strong className="text-white/90">By You:</strong> You may terminate this Agreement at
            any time by canceling your subscription and ceasing all use of the Software.
          </p>
          <p className="text-white/80 mt-2">
            <strong className="text-white/90">By Us:</strong> We may suspend or terminate your
            access for violation of this Agreement, non-payment, or for any reason with written
            notice.
          </p>
          <p className="text-white/80 mt-2">
            <strong className="text-white/90">Data Export Window:</strong> Upon termination, you
            will have 30 days to export Your Data through the platform&apos;s export features. After
            the 30-day period, Your Data will be permanently deleted from our systems within 90
            days, except where retention is required by law.
          </p>
          <p className="text-white/80 mt-2">
            <strong className="text-white/90">Survival:</strong> Sections relating to intellectual
            property, limitation of liability, indemnification, dispute resolution, and governing
            law survive termination of this Agreement.
          </p>

          {/* Section 11 */}
          <h2 className="text-xl font-semibold text-white mt-8 mb-3">
            11. Disclaimer of Warranties
          </h2>
          <p className="text-white/80">
            THE SOFTWARE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT
            WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED
            WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </p>
          <p className="text-white/80 mt-2">
            We do not warrant that the Software will be uninterrupted, error-free, or secure.
            Quotes, estimates, and measurements generated through the platform are approximations
            and should be verified by qualified professionals before use in contracts.
          </p>

          {/* Section 12 */}
          <h2 className="text-xl font-semibold text-white mt-8 mb-3">
            12. Limitation of Liability
          </h2>
          <p className="text-white/80">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, {COMPANY_NAME} SHALL NOT BE LIABLE FOR ANY
            INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF
            PROFITS, REVENUE, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR
            USE OF OR INABILITY TO USE THE SOFTWARE.
          </p>
          <p className="text-white/80 mt-2">
            Our total aggregate liability for all claims relating to the Software shall not exceed
            the greater of (a) the amount you paid us in the 12 months preceding the claim, or (b)
            $100 USD.
          </p>

          {/* Section 13 */}
          <h2 className="text-xl font-semibold text-white mt-8 mb-3">13. Indemnification</h2>
          <p className="text-white/80">
            You agree to indemnify and hold harmless {COMPANY_NAME}, its officers, directors,
            employees, and agents from any claims, damages, losses, liabilities, and expenses
            (including attorneys&apos; fees) arising from your use of the Software, violation of
            this Agreement, or infringement of any third-party rights.
          </p>

          {/* Section 14 */}
          <h2 className="text-xl font-semibold text-white mt-8 mb-3">14. Dispute Resolution</h2>
          <p className="text-white/80">
            Any dispute arising from or relating to this Agreement shall first be attempted to be
            resolved through good-faith negotiation between the parties. If the dispute cannot be
            resolved within 30 days of written notice, either party may pursue resolution through
            binding arbitration administered by the American Arbitration Association under its
            Commercial Arbitration Rules, conducted in Sheridan County, Wyoming.
          </p>

          {/* Section 15 */}
          <h2 className="text-xl font-semibold text-white mt-8 mb-3">15. Governing Law</h2>
          <p className="text-white/80">
            This Agreement shall be governed by and construed in accordance with the laws of the
            State of Wyoming, United States, without regard to its conflict-of-law provisions. Any
            legal proceedings not subject to arbitration shall be brought in the state or federal
            courts located in Sheridan County, Wyoming.
          </p>

          {/* Section 16 */}
          <h2 className="text-xl font-semibold text-white mt-8 mb-3">16. Severability</h2>
          <p className="text-white/80">
            If any provision of this Agreement is found to be invalid or unenforceable, the
            remaining provisions shall continue in full force and effect. The invalid provision
            shall be modified to the minimum extent necessary to make it valid and enforceable.
          </p>

          {/* Section 17 */}
          <h2 className="text-xl font-semibold text-white mt-8 mb-3">
            17. Changes to This Agreement
          </h2>
          <p className="text-white/80">
            We may update this EULA from time to time. We will notify you of material changes by
            email or by posting a notice on our website at least 30 days before the changes take
            effect. Continued use of the Software after changes constitutes acceptance of the
            updated Agreement.
          </p>

          {/* Section 18 */}
          <h2 className="text-xl font-semibold text-white mt-8 mb-3">18. Entire Agreement</h2>
          <p className="text-white/80">
            This Agreement, together with our{' '}
            <a href="/privacy" className="text-emerald-400 hover:underline">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="/terms" className="text-emerald-400 hover:underline">
              Terms of Service
            </a>
            , constitutes the entire agreement between you and {COMPANY_NAME} regarding the Software
            and supersedes all prior agreements and understandings.
          </p>

          {/* Section 19 */}
          <h2 className="text-xl font-semibold text-white mt-8 mb-3">19. Contact Us</h2>
          <p className="text-white/80">
            If you have questions about this End User License Agreement, please contact us:
          </p>
          <ul className="text-white/70 space-y-1">
            <li>
              <strong className="text-white/90">Email:</strong>{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-emerald-400 hover:underline">
                {SUPPORT_EMAIL}
              </a>
            </li>
            <li>
              <strong className="text-white/90">Address:</strong> {COMPANY_NAME}, {COMPANY_ADDRESS}
            </li>
          </ul>
        </div>
      </section>
    </>
  );
};
